import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { api, type NaradaSchema } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ZespolAsk from "@/components/zespol/ZespolAsk";
import { ZespolLetter, ZespolWaiting } from "@/components/zespol/ZespolLetter";
import ZespolTeamStrip from "@/components/zespol/ZespolTeamStrip";
import {
  FALLBACK_ROSTER,
  FALLBACK_THEMES,
  statusLabel,
} from "@/components/zespol/labels";
import { OpCallout } from "@/components/op/OpCallout";
import { OP_RESULT_CARD } from "@/lib/opSurfaces";
import { cn } from "@/lib/utils";

function schemaDefaults(schema?: NaradaSchema | null): string[] {
  return schema?.default_area_ids ? [...schema.default_area_ids] : [];
}

export default function ZespolAI() {
  const qc = useQueryClient();
  const rosterQ = useQuery({
    queryKey: ["ai-team-roster"],
    queryFn: api.listAgents,
  });
  const catalogQ = useQuery({
    queryKey: ["ai-team-catalog"],
    queryFn: api.naradaCatalog,
  });
  const listQ = useQuery({
    queryKey: ["ai-team-discussions"],
    queryFn: api.listDiscussions,
    refetchInterval: (q) =>
      (q.state.data || []).some((d) => d.status === "running") ? 3000 : false,
  });

  const roster = rosterQ.data && rosterQ.data.length > 0 ? rosterQ.data : FALLBACK_ROSTER;
  const schemas = catalogQ.data?.schemas || [];
  const groups = catalogQ.data?.groups || [];
  const themes =
    catalogQ.data?.themes && catalogQ.data.themes.length > 0
      ? catalogQ.data.themes
      : FALLBACK_THEMES;
  const running = (listQ.data || []).find((d) => d.status === "running") || null;
  const archive = (listQ.data || []).filter((d) => d.status !== "running");

  const [themeId, setThemeId] = useState("");
  const [schemaId, setSchemaId] = useState("");
  const [areaIds, setAreaIds] = useState<string[]>([]);
  const [question, setQuestion] = useState("");
  const [openId, setOpenId] = useState<number | null>(null);
  const [formError, setFormError] = useState("");

  const selectedTheme = useMemo(
    () => themes.find((row) => row.id === themeId) || themes[0] || null,
    [themes, themeId],
  );

  useEffect(() => {
    const theme = themes.find((row) => row.id === themeId) || themes[0];
    if (!theme) return;
    if (!themeId) setThemeId(theme.id);
    const current = schemas.find((row) => row.id === schemaId);
    const next =
      current ||
      schemas.find((row) => row.id === theme.default_schema_id) ||
      schemas.find((row) => theme.schema_ids.includes(row.id));
    if (!next) return;
    if (schemaId !== next.id) {
      setSchemaId(next.id);
      setAreaIds(schemaDefaults(next));
      return;
    }
    if (areaIds.length === 0 && (next.default_area_ids || []).length > 0) {
      setAreaIds(schemaDefaults(next));
    }
  }, [themeId, themes, schemas, schemaId, areaIds.length]);

  useEffect(() => {
    if (running?.id) setOpenId(running.id);
  }, [running?.id]);

  const detailQ = useQuery({
    queryKey: ["ai-team-discussion", openId],
    queryFn: () => api.getDiscussion(openId as number),
    enabled: openId != null,
    refetchInterval: running && openId === running.id ? 3000 : false,
  });

  const start = useMutation({
    mutationFn: () =>
      api.startNarada({
        schema_id: schemaId,
        area_ids: areaIds,
        question: question.trim(),
      }),
    onSuccess: (row) => {
      setFormError("");
      setOpenId(row.id);
      qc.invalidateQueries({ queryKey: ["ai-team-discussions"] });
    },
    onError: (err: Error) => {
      setFormError(err.message.replace(/^\d+:\s*/, ""));
    },
  });

  const pickTheme = (id: string) => {
    const nextTheme = themes.find((row) => row.id === id);
    setThemeId(id);
    const next =
      schemas.find((row) => row.id === nextTheme?.default_schema_id) ||
      schemas.find((row) => nextTheme?.schema_ids.includes(row.id));
    if (next) {
      setSchemaId(next.id);
      setAreaIds(schemaDefaults(next));
    }
  };

  const pickSchema = (id: string) => {
    const next = schemas.find((row) => row.id === id);
    setSchemaId(id);
    setAreaIds(schemaDefaults(next));
  };

  const toggleArea = (id: string) => {
    setAreaIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const openRow = archive.find((row) => row.id === openId) || null;
  const showLetter = Boolean(openRow && !running);

  return (
    <div className="zespol-page -mx-8 -my-8 min-h-screen">
      <header className="border-b border-slate-200 bg-white px-6 py-6 lg:px-8">
        <div className="mx-auto flex max-w-3xl items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Zapytaj zespół</h1>
            <p className="mt-1 text-sm text-slate-600">
              Zespół słucha Twojego pytania i wraca ze zdaniem. Ty decydujesz, co z nim zrobić.
            </p>
          </div>
          <Button
            asChild
            variant="outline"
            className="shrink-0 border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900"
          >
            <Link to="/karuzele">
              Studio karuzel
              <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-3xl space-y-12 px-6 py-8 lg:px-8">
        {running ? (
          <ZespolWaiting
            question={running.question || question}
            themeLabel={selectedTheme?.label}
          />
        ) : (
          <ZespolAsk
            question={question}
            onQuestion={setQuestion}
            themes={themes}
            schemas={schemas}
            groups={groups}
            themeId={themeId || themes[0]?.id || ""}
            onTheme={pickTheme}
            schemaId={schemaId}
            onSchema={pickSchema}
            areaIds={areaIds}
            onToggleArea={toggleArea}
            error={formError}
            pending={start.isPending}
            disabled={false}
            onSubmit={() => start.mutate()}
          />
        )}

        <ZespolTeamStrip roster={roster} />

        {showLetter && openRow && (
          <ZespolLetter row={openRow} detail={detailQ.data} />
        )}

        <section>
          <div className="flex items-end justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Poprzednie rozmowy</h2>
              <p className="mt-1 text-sm text-slate-600">
                Werdykty zostają tutaj. Nic nie idzie samo na tablicę.
              </p>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {archive.length === 0 && (
              <OpCallout variant="help" showLabel={false}>
                Jeszcze pusto. Pierwsze zdanie zespołu wyląduje tu.
              </OpCallout>
            )}
            {archive.map((row) => {
              const on = openId === row.id;
              return (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => setOpenId((cur) => (cur === row.id ? null : row.id))}
                  className={cn(
                    "w-full px-4 py-3 text-left transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30",
                    on ? OP_RESULT_CARD.selected : OP_RESULT_CARD.idle,
                  )}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium text-slate-900">
                      {row.schema_label || "Rozmowa"}
                    </span>
                    <Badge variant="secondary" className="bg-slate-50 text-slate-600">
                      {statusLabel(row.status || "")}
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm text-slate-500">{row.question}</p>
                  {row.verdict_preview && (
                    <p className="mt-2 line-clamp-2 text-sm text-slate-700">{row.verdict_preview}</p>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
